import { Box } from '@chakra-ui/react'
import { forwardRef, useCallback, useRef, useState } from 'react'

import { BaseInput, BaseInputProps } from '../BaseInput'

const INITIAL_HEIGHT_PX = 14
const MIN_HEIGHT_PX = 14
const MAX_HEIGHT_PX = 400

export type TextAreaInputProps = BaseInputProps

export const TextAreaInput = forwardRef<HTMLInputElement, BaseInputProps>(
  (props, inputTextRef) => {
    const [heightPx, setHeightPx] = useState(INITIAL_HEIGHT_PX)
    const dragRef = useRef<{ startY: number; startHeight: number } | null>(null)

    const handleCornerMouseDown = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        dragRef.current = { startY: e.clientY, startHeight: heightPx }
        const onMove = (moveEvent: MouseEvent) => {
          if (!dragRef.current) return
          const dy = moveEvent.clientY - dragRef.current.startY
          const next = Math.min(
            MAX_HEIGHT_PX,
            Math.max(MIN_HEIGHT_PX, dragRef.current.startHeight + dy),
          )
          setHeightPx(next)
        }
        const onUp = () => {
          dragRef.current = null
          window.removeEventListener('mousemove', onMove)
          window.removeEventListener('mouseup', onUp)
        }
        window.addEventListener('mousemove', onMove)
        window.addEventListener('mouseup', onUp)
      },
      [heightPx],
    )

    return (
      <Box position="relative" width="100%">
        <BaseInput
          ref={inputTextRef}
          as="textarea"
          resize="none"
          style={{
            height: `${heightPx}px`,
            minHeight: `${heightPx}px`,
          }}
          {...props}
        />
        <Box
          position="absolute"
          bottom="4px"
          right="4px"
          width="20px"
          height="20px"
          cursor="ns-resize"
          onClick={e => e.preventDefault()}
          onMouseDown={handleCornerMouseDown}
          aria-label="Resize"
          zIndex={2}
          sx={{
            backgroundImage: `linear-gradient(135deg, transparent 48%, rgba(0,0,0,0.4) 48%, rgba(0,0,0,0.4) 52%, transparent 52%),
              linear-gradient(135deg, transparent 48%, rgba(0,0,0,0.4) 48%, rgba(0,0,0,0.4) 52%, transparent 52%),
              linear-gradient(225deg, transparent 48%, rgba(0,0,0,0.4) 48%, rgba(0,0,0,0.4) 52%, transparent 52%)`,
            backgroundSize: '8px 8px',
            backgroundPosition: '100% 100%, 85% 85%, 100% 100%',
            backgroundRepeat: 'no-repeat',
            _dark: {
              backgroundImage: `linear-gradient(135deg, transparent 48%, rgba(255,255,255,0.55) 48%, rgba(255,255,255,0.55) 52%, transparent 52%),
                linear-gradient(135deg, transparent 48%, rgba(255,255,255,0.55) 48%, rgba(255,255,255,0.55) 52%, transparent 52%),
                linear-gradient(225deg, transparent 48%, rgba(255,255,255,0.55) 48%, rgba(255,255,255,0.55) 52%, transparent 52%)`,
              backgroundPosition: '100% 100%, 85% 85%, 100% 100%',
            },
          }}
        />
      </Box>
    )
  },
)
